import {
    AttachmentsAction,
    deleteAttachmentAction,
    replaceAttachmentAction,
    uploadAttachmentAction,
} from 'aos-services/src/core/attachments/actions'
import { AttachmentsState } from 'aos-services/src/core/attachments/state'
import { isImage } from 'aos-services/src/services/attachments/types/UploadedAttachment'
import { PreviewModal } from 'aos-ui/src/components/attachment/PreviewModal'
import { ZIndex } from 'aos-ui/src/components/base/Theme'
import { FileUpload } from 'aos-ui/src/components/form/FileUpload'
import {
    AttachmentButton,
    Box,
    CloseGlyph,
    colors,
    Pressable,
    Spinner,
    Text,
} from 'aos-ui-universal'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface TaskAttachmentGridProps {
    attachmentsState: AttachmentsState
    attachmentsAction(a: AttachmentsAction): void
}

const tile =
    'size-[124px] overflow-hidden rounded border border-solid border-border-subtle bg-aos-bg-elevation'

export const TaskAttachmentGrid = ({
    attachmentsState,
    attachmentsAction,
}: TaskAttachmentGridProps) => {
    const { t } = useTranslation()
    const [previewIndex, setPreviewIndex] = useState(0)
    const [isPreviewOpen, setPreviewOpen] = useState(false)

    const { attachments, isUploading } = attachmentsState

    const openPreview = (index: number) => {
        setPreviewIndex(index)
        setPreviewOpen(true)
    }

    return (
        <Box>
            <Box className='flex-row flex-wrap gap-3'>
                {attachments.map((attachment, index) => (
                    <Box key={index} className={`relative ${tile}`}>
                        <Pressable
                            className='h-full w-full items-center justify-center'
                            accessibilityRole='button'
                            onPress={() => openPreview(index)}
                        >
                            {isImage(attachment) && attachment.link ? (
                                <img
                                    src={attachment.link}
                                    alt={attachment.name ?? ''}
                                    className='h-full w-full object-cover'
                                />
                            ) : (
                                <Text
                                    size='caption'
                                    tone='muted'
                                    numberOfLines={3}
                                    className='px-2 text-center'
                                >
                                    {attachment.name ?? ''}
                                </Text>
                            )}
                        </Pressable>
                        <Pressable
                            className='absolute right-1 top-1 rounded bg-aos-bg-surface p-1 hover:bg-aos-bg-hover-layer'
                            accessibilityRole='button'
                            onPress={() => attachmentsAction(deleteAttachmentAction(attachment))}
                        >
                            <CloseGlyph size={16} color={colors['aos-text'].default} />
                        </Pressable>
                    </Box>
                ))}

                {isUploading && (
                    <Box className={`items-center justify-center ${tile}`}>
                        <Spinner />
                    </Box>
                )}

                <Box className='relative'>
                    <AttachmentButton hint={`(${t('allowed-file-types', { ns: 'events' })})`} />
                    <FileUpload
                        onChange={files => attachmentsAction(uploadAttachmentAction(files))}
                    />
                </Box>
            </Box>

            <PreviewModal
                isOpen={isPreviewOpen}
                attachments={attachments}
                attachmentIndex={previewIndex}
                editable
                closeAction={() => setPreviewOpen(false)}
                updateAttachment={payload => attachmentsAction(replaceAttachmentAction(payload))}
                onIndexChange={setPreviewIndex}
                zIndex={ZIndex.AboveAppOverlay}
                disableFullScreen
            />
        </Box>
    )
}
